import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { AccordionMultiSelect, ModalControlModalProps, NakedButton, Tray } from '@frontend/design-system';
import type { AvailabilityHeaderFieldProps } from './availability-header.types';

type FiltersTrayProps = Pick<
  AvailabilityHeaderFieldProps,
  | 'isMultiLocation'
  | 'locationOptions'
  | 'locationRadioField'
  | 'timeRangeOptions'
  | 'timeRangeChecklistField'
  | 'operatories'
  | 'operatoryChecklistField'
  | 'providers'
  | 'providerChecklistField'
> & {
  modalProps: ModalControlModalProps;
};

export const FiltersTray = ({
  isMultiLocation,
  locationOptions,
  locationRadioField,
  timeRangeOptions,
  timeRangeChecklistField,
  operatories,
  operatoryChecklistField,
  providers,
  providerChecklistField,
  modalProps,
}: FiltersTrayProps) => {
  const { t } = useTranslation('schedule');

  const handleClearAll = () => {
    locationRadioField.onChange({ name: 'locationId', value: locationOptions[0]?.value ?? '' });
    timeRangeChecklistField.onChange({ name: 'timeRanges', value: [] });
    operatoryChecklistField.onChange({ name: 'operatoryIds', value: [] });
    providerChecklistField.onChange({ name: 'providerIds', value: [] });
    modalProps.onClose();
  };

  return (
    <Tray width='medium' {...modalProps}>
      <Tray.Header
        Buttons={
          <NakedButton onClick={modalProps.onClose} css={styles.closeButton} aria-label={t('Close filters')}>
            <Icon name='x' size={20} color='default' />
          </NakedButton>
        }
      >
        {t('Filters')}
      </Tray.Header>
      <Tray.Body css={styles.body}>
        <AccordionMultiSelect isMulti>
          {isMultiLocation && locationOptions.length > 1 && (
            <AccordionMultiSelect.Item
              title={t('Location')}
              iconName='location-small'
              items={locationOptions}
              fieldProps={{ ...locationRadioField, name: 'locationId' }}
              hideSearch
              isSingleSelect
              hideClear
            />
          )}
          <AccordionMultiSelect.Item
            title={t('Time of Day')}
            iconName='clock-small'
            items={timeRangeOptions}
            fieldProps={{ ...timeRangeChecklistField, name: 'timeRanges' }}
            selectAllLabel={t('Select All')}
            clearLabel={t('Clear')}
            hideSearch
          />
          {operatories.length > 0 && (
            <AccordionMultiSelect.Item
              title={t('Operatory')}
              iconName='department-small'
              items={operatories}
              fieldProps={{ ...operatoryChecklistField, name: 'operatoryIds' }}
              selectAllLabel={t('Select All')}
              clearLabel={t('Clear')}
            />
          )}
          {providers.length > 0 && (
            <AccordionMultiSelect.Item
              title={t('Provider')}
              iconName='user-small'
              items={providers}
              fieldProps={{ ...providerChecklistField, name: 'providerIds' }}
              selectAllLabel={t('Select All')}
              clearLabel={t('Clear')}
            />
          )}
        </AccordionMultiSelect>
      </Tray.Body>
      <Tray.Actions
        primaryLabel={t('Done')}
        onPrimaryClick={modalProps.onClose}
        secondaryLabel={t('Reset')}
        onSecondaryClick={handleClearAll}
      />
    </Tray>
  );
};

const styles = {
  closeButton: css`
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  body: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(2, 0)};
  `,
};
